import React, { useState, useEffect, useCallback } from 'react';
import { GripVertical, Plus, Trash2, Package } from 'lucide-react';
import { getProductList, type Product, type ProductListRequest } from '../../../utils/product';
import { generateAfter } from '../../../utils/fractionalIndex';
import type { OptionGroupProductLine } from './productOptionGroupFormTypes';

function recomputeSortOrders(lines: OptionGroupProductLine[]): OptionGroupProductLine[] {
  let prev: string | null = null;
  return lines.map((line) => {
    const sortOrder = generateAfter(prev);
    prev = sortOrder;
    return { ...line, sortOrder };
  });
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return '활성';
    case 'INACTIVE':
      return '비활성';
    case 'PENDING':
      return '대기중';
    case 'TERMINATED':
      return '종료';
    default:
      return status || '-';
  }
}

export interface ProductOptionGroupProductsTabProps {
  projectCode: string;
  brandCode: string;
  brandName?: string;
  lines: OptionGroupProductLine[];
  onLinesChange: (lines: OptionGroupProductLine[]) => void;
}

const ProductOptionGroupProductsTab: React.FC<ProductOptionGroupProductsTabProps> = ({
  projectCode,
  brandCode,
  brandName,
  lines,
  onLinesChange,
}) => {
  const [candidates, setCandidates] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (!projectCode || !brandCode) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const req: ProductListRequest = {
          page: 0,
          size: 0,
          status: 'ACTIVE',
          brandCode,
        };
        const res = await getProductList(projectCode, req);
        if (!cancelled) setCandidates(res.products ?? []);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : '상품 목록을 불러오지 못했습니다.');
          setCandidates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectCode, brandCode]);

  const selectedCodes = new Set(lines.map((l) => l.productCode));

  const normalizedFilter = filterText.trim().toLowerCase();
  const filteredCandidates =
    normalizedFilter.length === 0
      ? candidates
      : candidates.filter((p) => {
          const code = String(p.code ?? '').toLowerCase();
          const name = String(p.name ?? '').toLowerCase();
          return code.includes(normalizedFilter) || name.includes(normalizedFilter);
        });

  useEffect(() => {
    console.log('[포함 상품 렌더 상태]', {
      brandCode,
      lineCount: lines.length,
      lines,
    });
  }, [brandCode, lines]);

  const addProduct = (p: Product) => {
    if (selectedCodes.has(p.code)) return;
    const lastSo = lines.length ? lines[lines.length - 1].sortOrder : null;
    const sortOrder = generateAfter(lastSo);
    const row: OptionGroupProductLine = {
      productCode: p.code,
      productName: p.name,
      defaultSelected: false,
      defaultQty: 1,
      maxQty: 5,
      minQty: 1,
      sortOrder,
    };
    onLinesChange([...lines, row]);
  };

  const removeAt = (index: number) => {
    const next = lines.filter((_, i) => i !== index);
    onLinesChange(recomputeSortOrders(next));
  };

  const updateLine = (index: number, patch: Partial<OptionGroupProductLine>) => {
    const next = lines.map((line, i) => (i === index ? { ...line, ...patch } : line));
    onLinesChange(next);
  };

  const onDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = useCallback(
    (dropIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      const from = Number(e.dataTransfer.getData('text/plain'));
      if (Number.isNaN(from) || from === dropIndex) {
        setDragIndex(null);
        return;
      }
      const moved = moveItem(lines, from, dropIndex);
      onLinesChange(recomputeSortOrders(moved));
      setDragIndex(null);
    },
    [lines, onLinesChange]
  );

  const brandDisplay = brandName?.trim() ? `${brandName} (${brandCode})` : brandCode;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Package className="w-5 h-5" />
          {brandDisplay} 상품에서 추가
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          선택한 브랜드의 상품만 표시됩니다.
        </p>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="코드 또는 상품명으로 검색"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filteredCandidates.length}/{candidates.length}건
            </span>
            {filterText.trim() !== '' && (
              <button
                type="button"
                onClick={() => setFilterText('')}
                className="px-3 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                초기화
              </button>
            )}
          </div>
        </div>
        {!brandCode ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            옵션그룹 정보에서 브랜드를 선택한 뒤 상품을 추가할 수 있습니다.
          </div>
        ) : (
          <>
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            )}
            {error && (
              <p className="text-sm text-red-600 mb-2">{error}</p>
            )}
            {!loading && !error && (
              <div className="border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-600">코드</th>
                      <th className="px-3 py-2 text-left text-gray-600">상품명</th>
                      <th className="px-3 py-2 text-center text-gray-600">상태</th>
                      <th className="px-3 py-2 text-right text-gray-600">원가격</th>
                      <th className="px-3 py-2 text-right text-gray-600">판매가격</th>
                      <th className="px-3 py-2 text-right w-24">—</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredCandidates.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                          표시할 상품이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      filteredCandidates.map((p) => (
                        <tr key={p.code} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-gray-800">{p.code}</td>
                          <td className="px-3 py-2 text-gray-900">{p.name}</td>
                          <td className="px-3 py-2 text-center text-gray-700">{getStatusLabel(p.status)}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{Number(p.originalPrice || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{Number(p.salePrice || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              disabled={selectedCodes.has(p.code)}
                              onClick={() => addProduct(p)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-3 h-3" />
                              추가
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-2">포함 상품</h3>
        <p className="text-sm text-gray-500 mb-3">정렬 순서는 드래그로 순서 변경이 가능합니다.</p>
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-2 py-2" />
                <th className="px-2 py-2 text-left text-gray-600">상품</th>
                <th className="px-2 py-2 text-center text-gray-600 whitespace-nowrap">기본 선택</th>
                <th className="px-2 py-2 text-center text-gray-600">기본 수량</th>
                <th className="px-2 py-2 text-center text-gray-600">최대 수량</th>
                <th className="px-2 py-2 text-center text-gray-600">최소 수량</th>
                <th className="w-12 px-2 py-2" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    위 목록에서 상품을 추가하세요.
                  </td>
                </tr>
              ) : (
                lines.map((line, index) => (
                  <tr
                    key={line.productCode}
                    draggable
                    onDragStart={onDragStart(index)}
                    onDragOver={onDragOver}
                    onDrop={onDrop(index)}
                    className={`hover:bg-gray-50 ${dragIndex === index ? 'opacity-70' : ''}`}
                  >
                    <td className="px-2 py-2 cursor-grab active:cursor-grabbing text-gray-400">
                      <GripVertical className="w-5 h-5" />
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-medium text-gray-900">{line.productName}</div>
                      <div className="text-xs text-gray-500 font-mono">{line.productCode}</div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={line.defaultSelected}
                        onChange={(e) =>
                          updateLine(index, { defaultSelected: e.target.checked })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="number"
                        min={0}
                        value={line.defaultQty}
                        onChange={(e) =>
                          updateLine(index, { defaultQty: Number(e.target.value) })
                        }
                        className="mx-auto w-20 px-2 py-1 border border-gray-300 rounded text-center"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="number"
                        min={0}
                        value={line.maxQty}
                        onChange={(e) =>
                          updateLine(index, { maxQty: Number(e.target.value) })
                        }
                        className="mx-auto w-20 px-2 py-1 border border-gray-300 rounded text-center"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="number"
                        min={0}
                        value={line.minQty}
                        onChange={(e) =>
                          updateLine(index, { minQty: Number(e.target.value) })
                        }
                        className="mx-auto w-20 px-2 py-1 border border-gray-300 rounded text-center"
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeAt(index)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="제거"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductOptionGroupProductsTab;